import { apiFetchRaw } from '@/lib/api';
import { offlineDb } from './db';
import {
  currentOwner,
  notifyOfflineChange,
  reportStorageFailure,
  isTemporaryFailure,
  cacheGeneration,
} from './cache';
import type { OfflineBundle } from './domainTypes';
import { setServerConnection } from './operations';

const memory = new Map<number, OfflineBundle>();
const permissionKey = (bundle: OfflineBundle) => [...bundle.unavailable].sort().join('|');
const preparations = new Map<number, Promise<void>>();
export function currentBundle(): OfflineBundle | null {
  const owner = currentOwner();
  return owner == null ? null : (memory.get(owner) ?? null);
}
export function isPreparing() {
  const owner = currentOwner();
  return owner != null && preparations.has(owner);
}
export function validateBundle(bundle: OfflineBundle, owner: number) {
  if (
    !bundle ||
    bundle.schema_version !== 1 ||
    bundle.calculation_version !== 1 ||
    !bundle.complete ||
    bundle.owner_id !== owner ||
    !Number.isSafeInteger(bundle.explotacion_id) ||
    bundle.explotacion_id <= 0 ||
    !bundle.revisions ||
    !bundle.catalogos ||
    !bundle.iot ||
    !Array.isArray(bundle.unavailable) ||
    ![
      'naves',
      'camadas',
      'lotes',
      'entradas',
      'registros',
      'observaciones',
      'pesajes',
      'entregas',
      'salidas',
      'liquidaciones',
    ].every((key) => Array.isArray(bundle.tables?.[key as keyof OfflineBundle['tables']]))
  )
    throw new Error('La copia descargada está incompleta o pertenece a otra sesión.');
}
export async function getBundle(owner = currentOwner()): Promise<OfflineBundle | null> {
  if (owner == null || !offlineDb.bundles) return null;
  const record = await offlineDb.bundles.get(String(owner));
  if (!record) return null;
  validateBundle(record.data, owner);
  if (currentOwner() !== owner) throw new Error('La sesión ha cambiado.');
  const previous = memory.get(owner);
  memory.set(owner, record.data);
  if (
    previous &&
    (permissionKey(previous) !== permissionKey(record.data) ||
      previous.explotacion_id !== record.data.explotacion_id)
  )
    window.dispatchEvent(new Event('offline:permissions-changed'));
  return record.data;
}
export async function saveBundle(bundle: OfflineBundle, owner = currentOwner()) {
  if (owner == null || currentOwner() !== owner) throw new Error('La sesión ha cambiado.');
  validateBundle(bundle, owner);
  await offlineDb.transaction(
    'rw',
    offlineDb.bundles,
    offlineDb.mutations,
    offlineDb.snapshots,
    offlineDb.mappings,
    async () => persistBundle(bundle, owner),
  );
}
async function persistBundle(bundle: OfflineBundle, owner: number) {
  const existing = await offlineDb.bundles.get(String(owner));
  // A preparation started before a confirmed write must not publish older
  // sources when its response arrives after the receipt.
  if (
    existing &&
    existing.data.explotacion_id === bundle.explotacion_id &&
    Date.parse(existing.data.prepared_at) > Date.parse(bundle.prepared_at)
  )
    return;
  if (
    existing &&
    (existing.data.explotacion_id !== bundle.explotacion_id ||
      permissionKey(existing.data) !== permissionKey(bundle))
  ) {
    for (const row of await offlineDb.snapshots.where('ownerId').equals(owner).toArray())
      await offlineDb.snapshots.delete(row.id);
  }
  await offlineDb.bundles.put({
    id: String(owner),
    ownerId: owner,
    generation: crypto.randomUUID(),
    data: bundle,
  });
  for (const row of await offlineDb.mutations.where('ownerId').equals(owner).toArray()) {
    if (row.explotacionId !== bundle.explotacion_id) {
      await offlineDb.mutations.put({
        ...row,
        status: 'error',
        actual: undefined,
        message:
          'Pertenece a la explotación anterior. Exporta estos cambios antes de descartarlos.',
      });
      continue;
    }
    if (
      row.legacy &&
      row.status === 'conflict' &&
      !row.actual &&
      row.explotacionId === bundle.explotacion_id
    )
      await offlineDb.mutations.put({ ...row, actual: bundle });
  }
  // Only publish memory after a successful independent commit. Callers inside a
  // larger transaction publish through getBundle after the transaction finishes.
}
export async function prepareOfflineBundle() {
  const owner = currentOwner();
  if (owner == null || !offlineDb.bundles) return;
  const existing = preparations.get(owner);
  if (existing) return existing;
  const preparation = (async () => {
    const startedGeneration = await cacheGeneration(owner);
    const bundle = await apiFetchRaw<OfflineBundle>('/v1/offline/bootstrap', {
      expectedOwnerId: owner,
    });
    setServerConnection('online');
    await offlineDb.transaction(
      'rw',
      offlineDb.bundles,
      offlineDb.mutations,
      offlineDb.snapshots,
      offlineDb.mappings,
      async () => {
        const current = await offlineDb.bundles.get(String(owner));
        if (
          current &&
          current.generation !== startedGeneration &&
          current.data.explotacion_id !== bundle.explotacion_id
        )
          return;
        await saveBundle(bundle, owner);
      },
    );
    await getBundle(owner);
    notifyOfflineChange();
  })()
    .catch((error) => {
      if (currentOwner() === owner) {
        if (isTemporaryFailure(error))
          setServerConnection(navigator.onLine === false ? 'offline' : 'unavailable');
        reportStorageFailure(error);
      }
      throw error;
    })
    .finally(() => {
      preparations.delete(owner);
      if (currentOwner() === owner) notifyOfflineChange();
    });
  preparations.set(owner, preparation);
  notifyOfflineChange();
  return preparation;
}
